<!-- UiAction.vue amélioré -->
<template>
    <!-- Cas simple : bouton ou lien unique -->
    <template v-if="!content.dropdown">
        <NuxtLink
            v-if="content.url"
            :to="content.url"
            :class="[buttonClasses, modifierClasses, shapeClasses, isDisabled ? 'opacity-30 pointer-events-none' : '']"
            :target="content.target ?? '_self'"
            :aria-label="ariaLabel"
            class="inline-flex gap-1 items-center transition-all duration-300 border"
            @click="track"
        >
            <i
                v-if="content.icon"
                class="fas text-sm leading-none"
                :class="content.icon"
                aria-hidden="true"
            ></i>
            <img
                v-else-if="content.picture"
                :src="content.picture"
                alt="peppol logo"
                class="w-[16px] h-[16px] object-contain"
            />
            <span
                v-if="content.label"
                class="text-center font-inter font-medium leading-none whitespace-nowrap"
            >
                {{ $t(content.label) }}
            </span>
            <!-- Trailing content (e.g. upsell badge) -->
            <slot />
        </NuxtLink>
        <button
            v-else
            :class="[buttonClasses, modifierClasses, shapeClasses, isDisabled ? 'opacity-30 pointer-events-none' : '']"
            type="button"
            :aria-label="ariaLabel"
            class="relative inline-flex gap-1 items-center transition-all duration-300 overflow-hidden border"
            @click="$emit('click', $event)"
        >
            <Loader v-if="isLoading" />

            <i
                v-if="content.icon"
                class="fas text-sm leading-none"
                :class="content.icon"
                aria-hidden="true"
            ></i>

            <span
                v-if="content.label"
                class="text-center font-inter font-medium leading-none whitespace-nowrap"
            >
                {{ $t(content.label) }}
            </span>
            <!-- Trailing content (e.g. upsell badge) -->
            <slot />
        </button>
    </template>

    <!-- Cas dropdown : bouton avec menu -->
    <div
        v-else
        class="flex relative"
    >
        <!-- Bouton principal (optionnel) -->
        <NuxtLink
            v-if="content.url"
            :to="content.url"
            :class="[buttonClasses, modifierClasses, shapeClasses, 'rounded-r-none !pr-4']"
            class="inline-flex gap-1 items-center transition-all duration-300 border border-r-0"
        >
            <i
                v-if="content.icon"
                class="fas text-sm leading-none"
                :class="content.icon"
                aria-hidden="true"
            ></i>

            <span
                v-if="content.label"
                class="text-center font-inter font-medium leading-none whitespace-nowrap hidden sm:inline"
            >
                {{ $t(content.label) }}
            </span>
        </NuxtLink>

        <!-- Bouton dropdown -->
        <button
            :class="[buttonClasses, 'rounded-l-none']"
            class="flex justify-center items-center px-3 py-2 border rounded-md transition-all duration-300"
            @click="dropdownOpen = !dropdownOpen"
        >
            <i
                class="fa-solid text-[13px]"
                :class="dropdownOpen ? 'fa-caret-up' : 'fa-caret-down'"
            ></i>
        </button>

        <!-- Menu dropdown -->
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div
                v-if="dropdownOpen"
                v-click-outside="closeDropdown"
                class="absolute w-80 top-full mt-2 z-20 right-0 rounded-lg p-3 bg-white shadow-lg"
            >
                <template
                    v-for="(item, index) in content.dropdown.items"
                    :key="index"
                >
                    <NuxtLink
                        v-if="item.url"
                        :to="item.url"
                        class="block px-6 py-3 cursor-pointer rounded-md hover:bg-gray-100 text-sm"
                        @click="dropdownOpen = false"
                    >
                        <i
                            v-if="item.icon"
                            class="fas mr-2"
                            :class="item.icon"
                        ></i>
                        {{ $t(item.label) }}
                    </NuxtLink>
                    <button
                        v-else
                        class="w-full text-left px-6 py-3 cursor-pointer rounded-md hover:bg-gray-100 text-sm"
                        @click="handleDropdownAction(item)"
                    >
                        <i
                            v-if="item.icon"
                            class="fas mr-2"
                            :class="item.icon"
                        ></i>
                        {{ $t(item.label) }}
                    </button>
                </template>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
    import type { Action, ActionModifiers, ActionShapes } from '@/app/types/components/global/Action';
    import type { DropdownItem } from '@/app/types/components/global/Dropdown';
    import type { Tracking } from '@/app/types/Tracking';

    const props = defineProps({
        content: {
            type: Object as PropType<Action>,
            required: true,
        },
        modifier: {
            type: String as PropType<ActionModifiers>,
            required: false,
            default: 'medium',
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        tracking: {
            type: Object as PropType<Tracking>,
            required: false,
            default: null,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        shape: {
            type: String as PropType<ActionShapes>,
            required: false,
            default: 'square',
        },
        /**
         * Accessible name for an icon-only button.
         *
         * Needed as a real prop because this component has several root nodes, so Vue
         * disables automatic attribute inheritance: an `aria-label` written on the tag
         * is silently dropped, and icon-only buttons end up with no name at all.
         * Additive — no existing caller passes it.
         */
        ariaLabel: {
            type: String,
            required: false,
            default: undefined,
        },
    });

    const emit = defineEmits(['click']);

    const dropdownOpen = ref(false);

    const buttonClasses = computed(() => {
        const type = props.content.type || 'main';
        const baseClasses = 'shadow-[0px_1px_1px_0px_rgba(0,0,0,0.1)]';

        const classes = {
            main: 'text-black bg-branding-brand-yellow hover:bg-branding-brand-yellow-hover border-branding-brand-border-yellow ' + baseClasses,
            light: 'text-gray-700 hover:bg-gray-200 border-action-gray bg-white ' + baseClasses,
            dark: 'text-white bg-action-black hover:bg-gray-900 border-gray-800 ' + baseClasses,
            gray: 'text-gray-400 hover:bg-gray-200 border-action-gray bg-branding-smoke border-none ' + baseClasses,
            link: 'text-gray-500 hover:text-gray-900 border-none',
            lightDark: 'text-white hover:bg-gray-900 border-gray-400 ' + baseClasses,
            shadow: 'text-gray-700 hover:bg-gray-200 border border-gray-200 bg-white ' + baseClasses,
            ai: 'btn-ai-gradient text-[#1582C8] bg-white ' + baseClasses,
            ia: 'text-white  bg-ia-button-gradient hover:opacity-90 border-branding-brand-border-yellow ' + baseClasses,
            success: 'text-white bg-branding-green hover:bg-branding-green/90 border-branding-green ' + baseClasses,
            error: 'text-white bg-branding-red hover:bg-branding-red/90 border-branding-red ' + baseClasses,
            // Claude Design's mobile document-form fills: a lighter yellow than `main`
            // (`--brand-yellow` #FFD033 on a #FFC400 border) and a plain white surface
            // on the design-system control border. Both carry `--shadow-xs` instead of
            // `baseClasses`, whose shadow is heavier than the design's.
            brand: 'text-zinc-900 bg-branding-brand-yellow-fill hover:bg-branding-brand-yellow border-branding-brand-yellow shadow-ds-xs',
            surface: 'text-zinc-700 hover:bg-zinc-50 border-zinc-300 bg-white shadow-ds-xs',
            dangerSoft: 'text-branding-danger-strong bg-white hover:bg-branding-danger-soft border-branding-danger-soft shadow-ds-xs',
        };

        return classes[type];
    });

    const isIconOnly = computed(() => props.shape === 'round' && !props.content.label);

    // Additive: `square` and `round` keep the exact classes they had before the two
    // design-system radii were added, so no existing caller changes.
    const SHAPE_CLASSES: Record<ActionShapes, string> = {
        square: 'rounded-md',
        round: 'rounded-full',
        soft: 'rounded-lg',
        softLg: 'rounded-[10px]',
    };

    const shapeClasses = computed(() => SHAPE_CLASSES[props.shape] ?? 'rounded-md');

    const modifierClasses = computed(() => {
        // Icon-only round: equal padding for a perfect circle
        if (isIconOnly.value) {
            const iconSizes = {
                big: 'p-3 text-base',
                medium: 'p-2.5 text-sm',
                small: 'p-2 text-xs',
                full: 'p-3 text-base',
                shadowfull: 'p-3 text-base',
            };
            return iconSizes[props.modifier || 'medium'];
        }

        if (!props.modifier) {
            return 'py-2 px-3 text-[13px]';
        }

        const modifiers = {
            big: 'py-2.5 px-3 text-sm md:py-3 md:px-4 md:text-base',
            medium: 'py-[5px] px-4 text-xs md:py-2.5 md:px-3 md:text-sm py-2.5 px-3 text-sm',
            small: 'py-[5px] px-4 text-xs',
            compact: 'py-1.5 px-2.5 text-xs',
            iconSquare: 'h-7 w-7 p-0 justify-center text-xs [&>i]:text-xs',
            full: 'w-full py-3 px-4 text-base justify-center',
            fullSmall: 'w-full py-[5px] px-4 text-xs justify-center',
            fullMedium: 'w-full py-2.5 px-4 text-sm justify-center',
            shadowfull: 'w-full py-3 px-4 text-base justify-center shadow-[0_1px_2px_0_rgba(0,0,0,0.10)]',
            // Fixed-height controls of Claude Design's mobile document form. Heights
            // are explicit rather than padding-derived because the design pins them
            // (34/40/48px) regardless of label length, and `gap-2` overrides the
            // button's own `gap-1` to reach the design's 8px icon-to-label gap.
            dsIconSm: 'h-[34px] w-[34px] p-0 justify-center text-[13px]',
            dsIconMd: 'h-10 w-10 p-0 justify-center text-[13px]',
            dsIconLg: 'h-12 w-12 p-0 justify-center text-[15px]',
            dsLg: 'h-12 px-4 gap-2 text-sm justify-center',
            dsFullMd: 'h-10 w-full px-3 gap-2 text-sm justify-center',
            dsFullLg: 'h-12 w-full px-4 gap-2 text-[15px] justify-center',
        };

        return modifiers[props.modifier];
    });

    const closeDropdown = () => {
        dropdownOpen.value = false;
    };

    const handleDropdownAction = (item: DropdownItem) => {
        if (item.action) {
            useEvent(item.action as keyof ApplicationEvents);
        }
        dropdownOpen.value = false;
    };

    // Tracking
    const { $tracker } = useNuxtApp();

    const track = () => {
        if (props.tracking) {
            $tracker.track(props.tracking.eventName, props.tracking.properties ?? {});
        }
    };
</script>
